import React, { useState, useEffect, useRef } from 'react';
import { Typography, Box, Button, Paper } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CustomModal from 'cms/core/shared/dialogs/CustomModal';
import JobExperienceForm from '../forms/JobExperienceForm';
import useJobExperiences from '../../hooks/useJobExperiences';
import useProfile from '../../hooks/UseProfile';
import DeleteDialog from 'core/components/shared/DeleteDialog';
import JobExperienceList from './JobExperienceList';
import { useQueryClient } from 'react-query';

const JobExperienceSection = ({ cv, compact = false, triggerAdd, readonly = false }) => {
    const queryClient = useQueryClient();
    const { jobExperiences, isLoading } = useJobExperiences(cv?.id);
    const { refetch: refreshProfile } = useProfile();
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedJobExperience, setSelectedJobExperience] = useState(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const prevTriggerRef = useRef(0);

    const handleAdd = () => {
        setSelectedJobExperience(null);
        setIsModalOpen(true);
    };

    useEffect(() => {
        if (triggerAdd && triggerAdd !== prevTriggerRef.current) {
            prevTriggerRef.current = triggerAdd;
            handleAdd();
        }
    }, [triggerAdd]);

    const handleEdit = (jobExperience) => {
        setSelectedJobExperience(jobExperience);
        setIsModalOpen(true);
    };

    const handleDelete = (jobExperience) => {
        setSelectedJobExperience(jobExperience);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        queryClient.invalidateQueries(['jobExperiences', cv?.id ?? null]);
        await refreshProfile();
        setIsDeleteModalOpen(false);
    };

    const content = (
        <>
            {error && (
                <Typography color="error" className="mb-4">
                    {error}
                </Typography>
            )}

            {isLoading ? (
                <Box display="flex" justifyContent="center" p={3}>
                    <Typography>Loading job experiences...</Typography>
                </Box>
            ) : (
                <JobExperienceList
                    jobExperiences={jobExperiences}
                    onEdit={readonly ? undefined : handleEdit}
                    onDelete={readonly ? undefined : handleDelete}
                    compact={compact}
                />
            )}

            <CustomModal
                open={isModalOpen}
                setOpen={setIsModalOpen}
                title={selectedJobExperience ? 'Edit Job Experience' : 'Add New Job Experience'}
            >
                <JobExperienceForm
                    id={selectedJobExperience?.id}
                    onCancel={() => setIsModalOpen(false)}
                    cvId={cv?.id}
                />
            </CustomModal>

            <DeleteDialog
                url={`/job/cv/jobexperience/${selectedJobExperience?.id}`}
                isOpen={isDeleteModalOpen}
                setOpen={setIsDeleteModalOpen}
                notifyDone={handleConfirmDelete}
                message="Are you sure you want to delete this job experience?"
            />
        </>
    );

    if (compact) {
        return content;
    }

    return (
        <Paper elevation={0} sx={{ p: 2.5, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1" fontWeight={600}>
                    Job Experience
                </Typography>
                {!readonly && (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={handleAdd}
                    >
                        Add
                    </Button>
                )}
            </Box>
            {content}
        </Paper>
    );
};

export default JobExperienceSection; 